import { useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { useAuth } from '../AuthProvider'
import { useAppPath } from '../nav'
import { BrandLogo } from '../../shared/BrandLogo'
import { moreNav, sidebarNav } from '../types'

function NavIcon({ name }: { name: string }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    'aria-hidden': true as const,
  }
  switch (name) {
    case 'Dashboard':
    case 'Home':
      return (
        <svg {...common}>
          <path
            d="M4 4h7v7H4V4Zm9 0h7v5h-7V4ZM4 13h7v7H4v-7Zm9 3h7v4h-7v-4Z"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinejoin="round"
          />
        </svg>
      )
    case 'Trading Accounts':
    case 'Accounts':
      return (
        <svg {...common}>
          <path
            d="M4 7.5A2.5 2.5 0 0 1 6.5 5h11A2.5 2.5 0 0 1 20 7.5v9a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 16.5v-9Z"
            stroke="currentColor"
            strokeWidth="1.75"
          />
          <path d="M4 10h16" stroke="currentColor" strokeWidth="1.75" />
          <path d="M8 15h3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
      )
    case 'Trading History':
    case 'History':
      return (
        <svg {...common}>
          <path
            d="M12 7v5l3 2"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.75" />
        </svg>
      )
    case 'Trade Now':
    case 'Launch MT5':
      return (
        <svg {...common}>
          <path
            d="M8 5h8a2 2 0 0 1 2 2v7H6V7a2 2 0 0 1 2-2Z"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinejoin="round"
          />
          <path d="M4 16h16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          <path d="M10 19h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
      )
    case 'Deposits':
      return (
        <svg {...common}>
          <path
            d="M12 4v10m0 0 3.5-3.5M12 14l-3.5-3.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M5 19h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
      )
    case 'Withdrawals':
      return (
        <svg {...common}>
          <path
            d="M12 20V10m0 0 3.5 3.5M12 10l-3.5 3.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M5 5h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
      )
    case 'Transactions':
    case 'Finance':
      return (
        <svg {...common}>
          <path
            d="M7 7h11l-2.5-2.5M17 17H6l2.5 2.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M6 12h12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
      )
    case 'Support':
      return (
        <svg {...common}>
          <path
            d="M6 9a6 6 0 0 1 12 0c0 3.5-2.4 4.8-2.4 7.2H8.4C8.4 13.8 6 12.5 6 9Z"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinejoin="round"
          />
          <path d="M10 19h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
      )
    case 'Profile':
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="3.25" stroke="currentColor" strokeWidth="1.75" />
          <path
            d="M5.5 19a6.5 6.5 0 0 1 13 0"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
          />
        </svg>
      )
    case 'More':
      return (
        <svg {...common}>
          <circle cx="6" cy="12" r="1.4" fill="currentColor" />
          <circle cx="12" cy="12" r="1.4" fill="currentColor" />
          <circle cx="18" cy="12" r="1.4" fill="currentColor" />
        </svg>
      )
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.75" />
        </svg>
      )
  }
}

function NavLabel({ label }: { label: string }) {
  return (
    <>
      <NavIcon name={label} />
      <span>{label}</span>
    </>
  )
}

export function ClientSidebar() {
  const { logout } = useAuth()
  const appPath = useAppPath()

  return (
    <aside className="cp-sidebar" aria-label="Client navigation">
      <div className="cp-sidebar__brand">
        <BrandLogo tone="light" />
        <span className="cp-sidebar__sub">Client Portal</span>
      </div>

      <nav className="cp-sidebar__nav">
        {sidebarNav.map((item) => (
          <NavLink
            key={item.to}
            to={appPath(item.to)}
            end={'end' in item ? Boolean(item.end) : false}
          >
            <NavLabel label={item.label} />
          </NavLink>
        ))}
      </nav>

      <div className="cp-sidebar__foot">
        <button type="button" className="pp-btn pp-btn--secondary pp-btn--sm" onClick={logout}>
          Logout
        </button>
      </div>
    </aside>
  )
}

/** Mobile slide-out drawer — same tabs as desktop sidebar. */
export function ClientMobileDrawer({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { logout } = useAuth()
  const appPath = useAppPath()

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="cp-drawer is-open">
      <button
        type="button"
        className="cp-drawer__backdrop"
        aria-label="Close menu"
        onClick={onClose}
      />
      <aside
        id="cp-mobile-drawer"
        className="cp-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
        <div className="cp-drawer__head">
          <div className="cp-drawer__brand">
            <BrandLogo tone="light" />
            <span className="cp-sidebar__sub">Client Portal</span>
          </div>
          <button type="button" className="cp-drawer__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <nav className="cp-drawer__nav">
          {sidebarNav.map((item) => (
            <NavLink
              key={item.to}
              to={appPath(item.to)}
              end={'end' in item ? Boolean(item.end) : false}
              onClick={onClose}
            >
              <NavLabel label={item.label} />
            </NavLink>
          ))}
        </nav>
        <div className="cp-drawer__foot">
          <button
            type="button"
            className="pp-btn pp-btn--secondary"
            onClick={() => {
              onClose()
              logout()
            }}
          >
            Logout
          </button>
        </div>
      </aside>
    </div>
  )
}

export function MoreMenu() {
  const { logout } = useAuth()
  const appPath = useAppPath()
  return (
    <section className="cp-page">
      <ul className="cp-more-list">
        {moreNav.map((item) => (
          <li key={item.label}>
            <NavLink to={appPath(item.to)}>{item.label}</NavLink>
          </li>
        ))}
        <li>
          <button type="button" onClick={logout}>
            Logout
          </button>
        </li>
      </ul>
    </section>
  )
}
